import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';

type Category = 'matched' | 'no_match' | 'duplicate' | 'invalid' | 'autre';

// Fixed categorical order/hues (validated adjacent-pair CVD separation for stacked bars, both
// modes) — never reorder per filter, never assign a hue by rank.
const CATEGORY_ORDER: Category[] = ['matched', 'no_match', 'duplicate', 'invalid', 'autre'];

const CATEGORY_LABELS: Record<Category, string> = {
    matched: 'Matchés',
    no_match: 'No match',
    duplicate: 'Dupliqués',
    invalid: 'Invalides',
    autre: 'Autres (bloqués...)',
};

const CATEGORY_COLORS: Record<Category, { light: string; dark: string }> = {
    matched: { light: '#2a78d6', dark: '#3987e5' },
    no_match: { light: '#eb6834', dark: '#d95926' },
    duplicate: { light: '#1baf7a', dark: '#199e70' },
    invalid: { light: '#eda100', dark: '#c98500' },
    autre: { light: '#e87ba4', dark: '#d55181' },
};

interface DailyStats {
    days: string[];
    series: Record<Category, number[]>;
}

type Granularity = 'day' | 'week';

interface GroupedStats {
    labels: string[];
    series: Record<Category, number[]>;
}

const PERIOD_OPTIONS = [
    { value: 7, label: '7 jours' },
    { value: 30, label: '30 jours' },
    { value: 90, label: '90 jours' },
];

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
    { value: 'day', label: 'Jour' },
    { value: 'week', label: 'Semaine' },
];

// Picks white or near-black label text by actual contrast against the segment's own fill —
// needed because the categorical hues span both dark (blue) and light (yellow, magenta) fills.
const readableTextColor = (bgHex: string): string => {
    const [r, g, b] = (bgHex.match(/[0-9a-f]{2}/gi) || []).map((h) => parseInt(h, 16) / 255);
    const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    const contrastWhite = 1.05 / (luminance + 0.05);
    const contrastBlack = (luminance + 0.05) / 0.05;
    return contrastWhite >= contrastBlack ? '#ffffff' : '#141412';
};

const formatDayLabel = (isoDay: string) => {
    const [, month, day] = isoDay.split('-');
    return `${day}/${month}`;
};

// Chunks the day-level series into 7-day buckets so one bar can represent one week — the last
// chunk is left partial rather than padded when the range isn't a multiple of 7.
const groupByWeek = (stats: DailyStats): GroupedStats => {
    const labels: string[] = [];
    const series: Record<Category, number[]> = { matched: [], no_match: [], duplicate: [], invalid: [], autre: [] };
    for (let i = 0; i < stats.days.length; i += 7) {
        const chunkDays = stats.days.slice(i, i + 7);
        labels.push(chunkDays.length > 1
            ? `${formatDayLabel(chunkDays[0])} - ${formatDayLabel(chunkDays[chunkDays.length - 1])}`
            : formatDayLabel(chunkDays[0]));
        CATEGORY_ORDER.forEach((category) => {
            series[category].push(stats.series[category].slice(i, i + 7).reduce((sum, v) => sum + v, 0));
        });
    }
    return { labels, series };
};

export default function BackOfficeLeadsKpi() {
    const [days, setDays] = useState(90);
    const [granularity, setGranularity] = useState<Granularity>('week');
    const [stats, setStats] = useState<DailyStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [isDark, setIsDark] = useState(false);
    const [showTable, setShowTable] = useState(false);

    useEffect(() => {
        setIsDark(document.documentElement.classList.contains('dark'));
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'));
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${CONFIG.apiUrl}/admin/leads/daily-stats`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { days },
                });
                setStats(response.data);
            } catch (error) {
                console.error('Error fetching leads daily stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [days]);

    const totals = CATEGORY_ORDER.reduce((acc, category) => {
        acc[category] = stats ? stats.series[category].reduce((sum, v) => sum + v, 0) : 0;
        return acc;
    }, {} as Record<Category, number>);
    const grandTotal = CATEGORY_ORDER.reduce((sum, category) => sum + totals[category], 0);

    const grouped: GroupedStats | null = useMemo(() => {
        if (!stats) return null;
        return granularity === 'week'
            ? groupByWeek(stats)
            : { labels: stats.days.map(formatDayLabel), series: stats.series };
    }, [stats, granularity]);

    const totalsPerBar = grouped
        ? grouped.labels.map((_, idx) => CATEGORY_ORDER.reduce((sum, category) => sum + grouped.series[category][idx], 0))
        : [];

    // Barres empilées par catégorie + courbe du total. La courbe, seule de son type, n'est pas
    // empilée sur les barres (Line.js), mais ApexCharts 3.41 l'additionne aux barres pour l'échelle :
    // d'où le max de l'axe Y fixé sur le plus haut total.
    const chartSeries = grouped
        ? [
              ...CATEGORY_ORDER.map((category) => ({
                  name: CATEGORY_LABELS[category],
                  type: 'bar',
                  data: grouped.series[category],
              })),
              { name: 'Total', type: 'line', data: totalsPerBar },
          ]
        : [];
    const maxTotal = Math.max(0, ...totalsPerBar);
    const yMax = maxTotal > 0 ? Math.ceil((maxTotal * 1.1) / 10) * 10 : undefined;
    const totalColor = isDark ? '#e8e6dc' : '#141412';
    const barStroke = isDark ? '#1a1a19' : '#fcfcfb';

    const chartOptions: any = {
        chart: {
            type: 'bar',
            stacked: true,
            toolbar: { tools: { download: false } },
        },
        colors: [...CATEGORY_ORDER.map((category) => (isDark ? CATEGORY_COLORS[category].dark : CATEGORY_COLORS[category].light)), totalColor],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: grouped && grouped.labels.length > 20 ? '80%' : '55%',
                borderRadius: 4,
                borderRadiusApplication: 'end',
                borderRadiusWhenStacked: 'last',
            },
        },
        // Each segment shows its own share of that bar's total leads (skipped when the segment
        // is empty, since an always-on "0%" on every bar would just be noise).
        dataLabels: {
            enabled: true,
            enabledOnSeries: CATEGORY_ORDER.map((_, index) => index),
            formatter: (val: number, opts: any) => {
                const total = totalsPerBar[opts.dataPointIndex] || 0;
                if (!val || !total) return '';
                return `${Math.round((val / total) * 100)}%`;
            },
            style: {
                fontSize: '11px',
                fontWeight: 600,
                colors: CATEGORY_ORDER.map((category) => readableTextColor(isDark ? CATEGORY_COLORS[category].dark : CATEGORY_COLORS[category].light)),
            },
            dropShadow: { enabled: false },
        },
        stroke: {
            show: true,
            curve: 'smooth',
            width: [...CATEGORY_ORDER.map(() => 1), 3],
            colors: [...CATEGORY_ORDER.map(() => barStroke), totalColor],
        },
        markers: { size: [...CATEGORY_ORDER.map(() => 0), 4], strokeWidth: 0, hover: { sizeOffset: 2 } },
        xaxis: {
            categories: grouped ? grouped.labels : [],
            labels: {
                style: { colors: isDark ? '#c3c2b7' : '#676767', fontSize: '11px' },
                rotate: -45,
            },
            axisBorder: { color: isDark ? '#2c2c2a' : '#F3EDE4' },
            axisTicks: { color: isDark ? '#2c2c2a' : '#F3EDE4' },
        },
        yaxis: {
            min: 0,
            max: yMax,
            forceNiceScale: true,
            labels: {
                formatter: (val: number) => Math.round(val).toString(),
                style: { colors: isDark ? '#c3c2b7' : '#676767', fontSize: '12px' },
            },
        },
        legend: {
            position: 'top',
            horizontalAlign: 'left',
            labels: { colors: isDark ? '#c3c2b7' : '#141412' },
        },
        grid: { borderColor: isDark ? '#2c2c2a' : '#F3EDE4', strokeDashArray: 4 },
        tooltip: {
            theme: isDark ? 'dark' : 'light',
            shared: true,
            intersect: false,
            // Le total de la période survolée est la ligne « Total » (courbe), en fin de liste.
            y: { formatter: (val: number) => `${val} lead${val > 1 ? 's' : ''}` },
        },
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            KPI Leads
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Volume de leads reçus par jour, ventilé par résultat de qualification
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex gap-1 rounded-lg border border-gray-200 p-1 dark:border-navy-600">
                            {GRANULARITY_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => setGranularity(opt.value)}
                                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                        granularity === opt.value
                                            ? 'bg-brand-500 text-white'
                                            : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-1 rounded-lg border border-gray-200 p-1 dark:border-navy-600">
                            {PERIOD_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => setDays(opt.value)}
                                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                        days === opt.value
                                            ? 'bg-brand-500 text-white'
                                            : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    <div className="rounded-lg border border-gray-200 p-3 dark:border-navy-600">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Total reçus</div>
                        <div className="mt-1 text-xl font-bold text-navy-700 dark:text-white">{grandTotal.toLocaleString()}</div>
                    </div>
                    {CATEGORY_ORDER.map((category) => (
                        <div key={category} className="rounded-lg border border-gray-200 p-3 dark:border-navy-600">
                            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                <span
                                    className="inline-block h-2 w-2 rounded-full"
                                    style={{ backgroundColor: isDark ? CATEGORY_COLORS[category].dark : CATEGORY_COLORS[category].light }}
                                />
                                {CATEGORY_LABELS[category]}
                            </div>
                            <div className="mt-1 text-xl font-bold text-navy-700 dark:text-white">
                                {totals[category].toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {grandTotal > 0 ? `${((totals[category] / grandTotal) * 100).toFixed(1)}%` : '—'}
                            </div>
                        </div>
                    ))}
                </div>

                {loading ? (
                    <div className="flex h-72 items-center justify-center text-sm text-gray-400">Chargement...</div>
                ) : !stats || grandTotal === 0 ? (
                    <div className="flex h-72 items-center justify-center text-sm text-gray-400">Aucune donnée sur cette période.</div>
                ) : (
                    <>
                        <div id="leads-kpi-chart">
                            <ReactApexChart options={chartOptions} series={chartSeries} type="line" height={380} />
                        </div>

                        <button
                            onClick={() => setShowTable((prev) => !prev)}
                            className="mt-3 text-sm font-medium text-brand-500 hover:underline"
                        >
                            {showTable ? 'Masquer le détail en tableau' : 'Voir le détail en tableau'}
                        </button>

                        {showTable && grouped && (
                            <div className="mt-3 overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 text-gray-500 dark:border-navy-600 dark:text-gray-400">
                                            <th className="py-2 pr-4">{granularity === 'week' ? 'Semaine' : 'Date'}</th>
                                            <th className="py-2 pr-4">Total</th>
                                            {CATEGORY_ORDER.map((category) => (
                                                <th key={category} className="py-2 pr-4">{CATEGORY_LABELS[category]}</th>
                                            ))}
                                            <th className="py-2 pr-4">% no match</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {grouped.labels.map((label, idx) => {
                                            const rowTotal = totalsPerBar[idx] || 0;
                                            const noMatchPct = rowTotal > 0 ? (grouped.series.no_match[idx] / rowTotal) * 100 : 0;
                                            return (
                                                <tr key={label} className="border-b border-gray-100 dark:border-navy-700">
                                                    <td className="py-2 pr-4 text-navy-700 dark:text-white">{label}</td>
                                                    <td className="py-2 pr-4 font-medium text-navy-700 dark:text-white">{rowTotal}</td>
                                                    {CATEGORY_ORDER.map((category) => (
                                                        <td key={category} className="py-2 pr-4 text-gray-600 dark:text-gray-300">
                                                            {grouped.series[category][idx]}
                                                        </td>
                                                    ))}
                                                    <td className="py-2 pr-4 text-gray-600 dark:text-gray-300">
                                                        {rowTotal > 0 ? `${noMatchPct.toFixed(1)}%` : '—'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
}
